import React, { createContext, useReducer, useState, useEffect, useContext } from "react";
import { SLActionStatus, SLLoadingStatus } from "../utilities/utilities";
import { RequireAtLeastOne } from "../services/TypeUtil";
import { UseSlEntryFromApiState, UseDataFromSubledgerState } from "../hooks/useDataFromSubledger";
import { ISLEntryResponse } from "../components/SLCreate/SLCreateSchemas";
import { SubledgerContext, SubledgerContextData } from "./SubledgerContext";
import WeightageCalculator, { IWeightageCalculatorArgs, IWeightageCalculator } from "../utilities/WeightageCalculator";
import { FatalError } from "../services/ErrorHelpers";

export type AllActions =
  | "save"
  | "post"
  | "sendToPoster"
  | "validate"
  | "clear"
  | "downloadTemplate"
  | "approve"
  | "needsClarification"
  | "recall"
  | "clearForm"
  | "addAttachment"
  | "removeAttachment";

/*  The CreateAndBatchContext is a context designed to control:
    - Actions on Page based on multiple variables: Roles, Status, Action Links and Weightages
    - Errors from UI Sections in Page
    - readOnly trigger

    The Consumer returns the following
    {
      getPermission(actionName) Function to fetch current status of Action, so that the Page Form can Consume
      entry: null | SLCreateSLBatchEntry | SLBatchEntry
      loadingStatus: idle | pending | resolved | rejected
      errors: {
        [collectorName: string]: string[] These are errors on the UI,
                                          if there are any the PageForm must allow clicking the Action
                                          but must block performing the Action
      }
      isReadOnly flag to be consumed by any child
    }
    Error Collectors:
      SLCreateSLBatch
      - "header": string[]
      - "posters": string[]
      - "comments": string[]
      - "lineItems": string[]
      SLBatch
      - "header": string[]
*/

export interface AllActionsOnCommandBar {
  save: SLActionStatus;
  post: SLActionStatus;
  sendToPoster: SLActionStatus;
  validate: SLActionStatus;
  approve: SLActionStatus;
  needsClarification: SLActionStatus;
  downloadTemplate: SLActionStatus;
  recall: SLActionStatus;
  clearForm: SLActionStatus;
  addAttachment: SLActionStatus;
  removeAttachment: SLActionStatus;
}

const commandsToEnableInitialState: AllActionsOnCommandBar = {
  save: SLActionStatus.Disabled,
  post: SLActionStatus.Disabled,
  sendToPoster: SLActionStatus.Disabled,
  validate: SLActionStatus.Disabled,
  approve: SLActionStatus.Disabled,
  needsClarification: SLActionStatus.Disabled,
  downloadTemplate: SLActionStatus.Disabled,
  recall: SLActionStatus.Disabled,
  clearForm: SLActionStatus.Disabled,
  addAttachment: SLActionStatus.Disabled,
  removeAttachment: SLActionStatus.Disabled
};

type SLCreateSLBatchProviderStateReducer = (
  state: AllActionsOnCommandBar,
  changes: RequireAtLeastOne<AllActionsOnCommandBar>
) => AllActionsOnCommandBar;

interface UIErrors {
  [collectorName: string]: boolean;
}

type SLCreateSLBatchUIErrorReducer = (
  state: UIErrors,
  changes: {
    collectorName: string;
    value: boolean;
  }
) => UIErrors;

interface ISLCreateSLBatchContext {
  getPermission: (actionName: AllActions) => SLActionStatus;
  isReadOnly: boolean;
  dataType: "SLCreate" | "SLBatch" | null;
  entry: UseSlEntryFromApiState["entry"];
  setErrors: (value: { collectorName: string; value: boolean }) => void;
  loadingStatus: SLLoadingStatus;
  hasErrors: () => boolean;
}

// eslint-disable-next-line
const noop = () => {};

const baseContext: ISLCreateSLBatchContext = {
  getPermission: () => SLActionStatus.Removed,
  dataType: null,
  hasErrors: () => true,
  setErrors: noop,
  isReadOnly: true,
  loadingStatus: SLLoadingStatus.Idle,
  entry: null
};

const SLCreateSLBatchContext = createContext<ISLCreateSLBatchContext>(baseContext);
const { Provider } = SLCreateSLBatchContext;

function setupSLCreateRules(
  endpointData: UseDataFromSubledgerState,
  userRoles: SubledgerContextData["userRoles"],
  actionLinks: SubledgerContextData["actionLinks"],
  setReadOnly: React.Dispatch<React.SetStateAction<boolean>>,
  setCommandPermissions: React.Dispatch<RequireAtLeastOne<AllActionsOnCommandBar>>
) {
  const commandPermission = (buttonName: keyof IWeightageCalculator, opts: IWeightageCalculatorArgs) => {
    if (WeightageCalculator[buttonName](actionLinks.weightages, opts)) {
      return SLActionStatus.Disabled;
    }
    return SLActionStatus.Enabled;
  };

  const entry = endpointData.entry as ISLEntryResponse | null;

  let optsForWeightageCalculator: IWeightageCalculatorArgs;
  if (entry) {
    optsForWeightageCalculator = {
      entryId: entry.slEntryId,
      rowWeightage: entry.rowWeightage
    };
    if (entry.hasWriteAccess && entry.jeStatus && entry.jeStatus !== 12) {
      setReadOnly(false);
    }
  } else {
    optsForWeightageCalculator = {
      entryId: 0,
      rowWeightage: 0
    };
    setReadOnly(false);
  }
  const commandPermissionAbridged = (buttonName: keyof IWeightageCalculator) =>
    commandPermission(buttonName, optsForWeightageCalculator);

  const commandPermissionForPostButton = () => {
    if (optsForWeightageCalculator.entryId === 0 && !userRoles.value.hasPostingAccess) {
      return SLActionStatus.Disabled;
    }

    return commandPermissionAbridged("disablePostButton");
  };

  setCommandPermissions({
    save: commandPermissionAbridged("disableSaveButton"),
    post: commandPermissionForPostButton(),
    sendToPoster: commandPermissionAbridged("disableSendToPosterButton"),
    validate: commandPermissionAbridged("disableValidateButton"),
    approve: commandPermissionAbridged("disableApproveButton"),
    needsClarification: commandPermissionAbridged("disableNeedsClarificationButton"),
    recall: commandPermissionAbridged("disableRecallButton"),
    clearForm: commandPermissionAbridged("disableClearFormButton"),
    addAttachment: commandPermissionAbridged("disableAttachmentsButton"),
    removeAttachment: commandPermissionAbridged("disableAttachmentsButton")
  });
}

function setupSLBatchRules(
  endpointData: UseDataFromSubledgerState,
  userRoles: SubledgerContextData["userRoles"],
  actionLinks: SubledgerContextData["actionLinks"],
  setReadOnly: React.Dispatch<React.SetStateAction<boolean>>,
  setCommandPermissions: React.Dispatch<RequireAtLeastOne<AllActionsOnCommandBar>>
) {
  if (!userRoles.value.hasPostingAccess) {
    setReadOnly(true);
  } else {
    setReadOnly(false);
  }

  const commandsEnabled = userRoles.value.hasPostingAccess ? SLActionStatus.Enabled : SLActionStatus.Disabled;
  setCommandPermissions({
    post: commandsEnabled,
    clearForm: commandsEnabled,
    addAttachment: commandsEnabled,
    removeAttachment: commandsEnabled
  });
}

interface SLCreateSLBatchProviderProps {
  endpointData: UseDataFromSubledgerState;
  children: React.ReactNode;
}

const SLCreateSLBatchProvider = (props: SLCreateSLBatchProviderProps): JSX.Element => {
  const { actionLinks, userRoles } = useContext(SubledgerContext);
  const [isReadOnly, setReadOnly] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [commandPermissions, setCommandPermissions] = useReducer<SLCreateSLBatchProviderStateReducer>(
    (previousState, changes) => ({
      ...previousState,
      ...changes
    }),
    commandsToEnableInitialState
  );

  const [errors, _setErrors] = useReducer<SLCreateSLBatchUIErrorReducer>((previousState, { collectorName, value }) => {
    previousState[collectorName] = value;
    return previousState;
  }, {});

  useEffect(() => {
    setLoadingStatus(SLLoadingStatus.Pending);
    return () => {
      setLoadingStatus(SLLoadingStatus.Idle);
      setCommandPermissions(commandsToEnableInitialState);
    };
  }, []);

  const getPermission = (commandName: AllActions) => {
    if (commandName in commandPermissions) {
      return commandPermissions[commandName as keyof typeof commandPermissions];
    }
    return SLActionStatus.Removed;
  };

  const hasErrors = () => {
    for (const error of Object.values(errors)) {
      if (error) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const actionsAreLoaded = actionLinks.status === SLLoadingStatus.Resolved;
    const rolesAreLoaded = userRoles.status === SLLoadingStatus.Resolved;
    const entryIsLoadedOrNull = props.endpointData.loadingStatus === SLLoadingStatus.Resolved;
    if (entryIsLoadedOrNull && actionsAreLoaded && rolesAreLoaded) {
      if (props.endpointData.dataType === "SLCreate") {
        setupSLCreateRules(props.endpointData, userRoles, actionLinks, setReadOnly, setCommandPermissions);
      } else if (props.endpointData.dataType === "SLBatch") {
        setupSLBatchRules(props.endpointData, userRoles, actionLinks, setReadOnly, setCommandPermissions);
      } else {
        throw new FatalError("Please setup SLCreateSLBatchContext for SLCreate or SLBatch.");
      }
      setLoadingStatus(SLLoadingStatus.Resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.endpointData.loadingStatus, actionLinks.status, userRoles.status, props.endpointData.entry]);

  const setErrors = ({ collectorName, value }: { collectorName: string; value: boolean }) => {
    _setErrors({ collectorName, value });
  };

  return (
    <Provider
      value={{
        isReadOnly,
        loadingStatus,
        getPermission,
        dataType: props.endpointData.dataType,
        entry: props.endpointData.entry,
        setErrors,
        hasErrors
      }}
    >
      {props.children}
    </Provider>
  );
};

export { SLCreateSLBatchProvider, SLCreateSLBatchContext };
